import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Chat as SlackIcon,
  ArrowBack as ArrowBackIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import SlackIntegrationService from '../services/SlackIntegrationService';

const SlackCallback = () => {
  const { code, state, error } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [integrationData, setIntegrationData] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check if user is authenticated
        const token = localStorage.getItem('token');
        if (!token) {
          setStatus('error');
          setMessage('User not authenticated. Please log in and try again.');
          return;
        }

        // Parse URL parameters
        const urlParams = new URLSearchParams(location.search);
        const codeParam = urlParams.get('code');
        const stateParam = urlParams.get('state');
        const errorParam = urlParams.get('error');

        if (errorParam) {
          setStatus('error');
          setMessage(`Slack authorization failed: ${errorParam}`);
          return;
        }

        if (!codeParam || !stateParam) {
          setStatus('error');
          setMessage('Missing required parameters from Slack callback');
          return;
        }

        console.log('Slack callback - completing OAuth with code:', codeParam?.substring(0, 10) + '...');

        // Complete OAuth flow
        const result = await SlackIntegrationService.handleOAuthCallback(codeParam, stateParam);
        
        if (result.status === 'success') {
          setStatus('success');
          setIntegrationData(result.data);
          setMessage('Slack integration completed successfully!');
        } else {
          setStatus('error');
          setMessage(result.error?.message || 'Failed to complete Slack integration');
        }
      } catch (error) {
        console.error('Slack callback error:', error);
        setStatus('error');
        setMessage(error.response?.data?.error?.message || error.message || 'Failed to complete Slack integration');
      }
    };

    handleCallback();
  }, [code, state, error, location.search]);

  const handleClose = () => {
    navigate('/aws-alb-manager');
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress size={60} sx={{ mb: 3, color: '#1976d2' }} />
            <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
              Completing Slack Integration...
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', textAlign: 'center' }}>
              Please wait while we connect your Slack workspace
            </Typography>
          </Box>
        );

      case 'success':
        return (
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50', mr: 2 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                  Slack Integration Successful!
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Your workspace is now connected
                </Typography>
              </Box>
            </Box>

            <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <SlackIcon sx={{ mr: 1, color: '#1976d2' }} />
                  Integration Details
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      Team/Workspace
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {integrationData?.teamName || 'Unknown'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      Channel
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {integrationData?.channelName || 'Default channel'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      Connected At
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {integrationData?.connectedAt ? 
                        new Date(integrationData.connectedAt).toLocaleString() : 
                        'Just now'
                      }
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                🎉 <strong>What's next?</strong><br />
                • Health alerts will be sent to your Slack channel<br />
                • You can test notifications from the Email Configuration page<br />
                • You can disconnect anytime from the same page
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={handleClose}
                sx={{
                  background: '#1976d2',
                  color: 'white',
                  fontWeight: 600,
                  px: 4,
                  '&:hover': {
                    background: '#1565c0',
                  },
                }}
              >
                Go to ALB Manager
              </Button>
            </Box>
          </Box>
        );

      case 'error':
        return (
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <ErrorIcon sx={{ fontSize: 48, color: '#f44336', mr: 2 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                  Slack Integration Failed
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Unable to connect your workspace
                </Typography>
              </Box>
            </Box>

            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Error:</strong> {message}
              </Typography>
            </Alert>

            <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Troubleshooting Steps
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{ margin: 0, paddingLeft: '1.5em' }}>
                    <li>Ensure you have admin permissions in your Slack workspace</li>
                    <li>Check that you approved the requested permissions</li>
                    <li>Try the integration again in a few minutes</li>
                    <li>Contact support if the issue persists</li>
                  </ul>
                </Typography>
                {message.includes('not authenticated') && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: '#856404', mb: 2 }}>
                      <strong>Authentication Required:</strong> You need to log in first before connecting Slack.
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => window.location.href = 'https://auth.cloudsanalytics.ai/login/continue?client_id=4u1928epr7vrrpeg5g5ehiudc2&response_type=code&scope=email+openid+phone&redirect_uri=https%3A%2F%2Fcodelens.cloudsanalytics.ai%2Fhome'}
                      sx={{
                        background: '#ffc107',
                        color: '#212529',
                        '&:hover': {
                          background: '#e0a800',
                        },
                      }}
                    >
                      Login First
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  borderColor: '#ddd',
                  color: '#666',
                  '&:hover': {
                    borderColor: '#bbb',
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                Back to ALB Manager
              </Button>
              <Button
                variant="contained"
                onClick={() => window.location.href = SlackIntegrationService.getOAuthUrl()}
                sx={{
                  background: '#1976d2',
                  color: 'white',
                  fontWeight: 600,
                  px: 4,
                  '&:hover': {
                    background: '#1565c0',
                  },
                }}
              >
                Try Again
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid #e0e0e0',
          backgroundColor: '#ffffff',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={handleClose}
            sx={{ mr: 1, color: '#666' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <SlackIcon sx={{ fontSize: 32, color: '#1976d2', mr: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#333' }}>
            Slack Integration
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Content */}
        {renderContent()}

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #eee' }}>
          <Typography variant="caption" sx={{ color: '#999', textAlign: 'center', display: 'block' }}>
            Secure OAuth integration with Slack • Permissions: channels:write, chat:write, incoming-webhook
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default SlackCallback;
